import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Store, Copy, RefreshCw, ExternalLink, CheckCircle2, KeyRound, Mail, History, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [gmailEmail, setGmailEmail] = useState("pranav@aaywon.com");
  const [gmailPwd, setGmailPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [impEmail, setImpEmail] = useState("");
  const [impPwd, setImpPwd] = useState("");
  const [impDays, setImpDays] = useState(15);
  const [importing, setImporting] = useState(false);
  const [senders, setSenders] = useState([]);
  const [senderPwd, setSenderPwd] = useState({});
  const [senderBusy, setSenderBusy] = useState("");

  const origin = window.location.origin;
  const enquiryUrl = `${origin}/enquiry`;
  const apiEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/public/leads`;

  const load = () => {
    api.get("/integrations/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/integrations/senders").then(({ data }) => setSenders(data.senders || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connectSender = async (email) => {
    const pwd = (senderPwd[email] || "").trim();
    if (!pwd) { toast.error("Enter the App Password for " + email); return; }
    setSenderBusy(email);
    try {
      await api.post("/integrations/senders/connect", { email, app_password: pwd });
      toast.success(`${email} connected — its sent emails will appear in its own Sent tab`);
      setSenderPwd((p) => ({ ...p, [email]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSenderBusy(""); }
  };

  const disconnectSender = async (email) => {
    setSenderBusy(email);
    try {
      await api.post("/integrations/senders/disconnect", { email });
      toast.success(`${email} disconnected`);
      load();
    } catch { toast.error("Failed to disconnect"); } finally { setSenderBusy(""); }
  };

  const copy = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  const connectGmail = async () => {
    if (!gmailEmail.trim() || !gmailPwd.trim()) { toast.error("Enter the Gmail address and App Password"); return; }
    setSaving(true);
    try {
      await api.post("/integrations/gmail/connect", { email: gmailEmail.trim(), app_password: gmailPwd.trim() });
      toast.success("Gmail connected — IndiaMART emails will auto-import");
      setGmailPwd("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const disconnectGmail = async () => {
    try {
      await api.post("/integrations/gmail/disconnect");
      toast.success("Gmail disconnected");
      load();
    } catch { toast.error("Failed to disconnect"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/gmail/sync");
      if (data.status === "success") toast.success(`Imported ${data.new_leads} new IndiaMART lead(s)`);
      else if (data.status === "skipped") toast.message("Connect Gmail first");
      else toast.error(`Sync: ${data.reason || "failed"}`);
      load();
    } catch (e) {
      toast.error("Sync failed");
    } finally { setSyncing(false); }
  };

  const importOnce = async () => {
    if (!impEmail.trim() || !impPwd.trim()) { toast.error("Enter the inbox email and its App Password"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/gmail/import-once", {
        email: impEmail.trim(), app_password: impPwd.trim(), days: Number(impDays) || 15,
      });
      if (data.status === "success") toast.success(`Imported ${data.new_leads} lead(s) from ${data.inbox} (last ${data.days} days)`);
      else toast.error(`Import: ${data.reason || "failed"}`);
      setImpPwd("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const [cleaning, setCleaning] = useState(false);
  const cleanupJunk = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/leads/cleanup-junk");
      toast.success(data.removed > 0
        ? `Removed ${data.removed} junk lead(s) (IndiaMART promo/system entries)`
        : "No junk leads found — your funnel is clean");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cleanup failed");
    } finally { setCleaning(false); }
  };


  const toggleEmailReminders = async (next) => {
    try {
      await api.post("/integrations/notifications", { email_reminders: next });
      toast.success(next ? "Email reminders enabled" : "Email reminders disabled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };


  const embedSnippet = `<!-- Lami Tube enquiry form -->
<form id="lt-enquiry">
  <input name="company_name" placeholder="Company" />
  <input name="contact_name" placeholder="Name" />
  <input name="phone" placeholder="Phone" required />
  <input name="email" placeholder="Email" />
  <input name="product_requirement" placeholder="Product requirement" />
  <input name="quantity" placeholder="Quantity" />
  <input name="city" placeholder="City" />
  <textarea name="message" placeholder="Message"></textarea>
  <button type="submit">Send Enquiry</button>
</form>
<script>
document.getElementById('lt-enquiry').addEventListener('submit', async function(e){
  e.preventDefault();
  const d = Object.fromEntries(new FormData(this).entries());
  await fetch('${apiEndpoint}', {method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(d)});
  this.innerHTML = '<p>Thank you! We will get back to you shortly.</p>';
});
</script>`;

  return (
    <div data-testid="integrations-page" className="max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Lead Capture</h1>
        <p className="text-slate-500 text-sm">Bring leads in automatically from your website and IndiaMART</p>
      </div>

      {/* Notification settings */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="notification-settings">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center"><Mail className="h-5 w-5 text-indigo-600" /></div>
          <div>
            <h3 className="font-display text-lg font-medium">Notifications</h3>
            <p className="text-xs text-slate-500">Control how overdue-lead reminders reach your team</p>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Email reminders</p>
            <p className="text-xs text-slate-500">Send overdue-lead reminders to the assigned member's email. In-app reminders always stay on.</p>
          </div>
          <button
            onClick={() => toggleEmailReminders(!status?.email_reminders_enabled)}
            data-testid="toggle-email-reminders"
            role="switch"
            aria-checked={!!status?.email_reminders_enabled}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${status?.email_reminders_enabled ? "bg-blue-600" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${status?.email_reminders_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Sending mailboxes — Sent-tab sync per sender */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="sending-mailboxes">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Mail className="h-5 w-5 text-emerald-600" /></div>
          <div>
            <h3 className="font-display text-lg font-medium">Sending mailboxes · Sent-tab sync</h3>
            <p className="text-xs text-slate-500">Connect each sender's Gmail App Password so emails they send appear in that account's own Sent tab</p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {senders.length === 0 && <p className="text-sm text-slate-400">No sender addresses configured.</p>}
          {senders.map((s) => (
            <div key={s.email} className="rounded-lg border border-slate-200 p-3" data-testid={`sender-row-${s.email}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-800">{s.email}</span>
                  {s.connected && !s.via_main && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Connected</span>
                  )}
                  {s.via_main && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">Main inbox</span>
                  )}
                  {!s.connected && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">Not connected</span>
                  )}
                </div>
                {s.connected && !s.via_main && (
                  <Button variant="outline" size="sm" disabled={senderBusy === s.email} onClick={() => disconnectSender(s.email)} data-testid={`sender-disconnect-${s.email}`} className="text-red-600">
                    Disconnect
                  </Button>
                )}
              </div>
              {(!s.connected || s.via_main) && (
                <div className="mt-2 flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <Label className="text-xs">Gmail App Password for {s.email}</Label>
                    <Input
                      type="password"
                      placeholder="16-character app password"
                      value={senderPwd[s.email] || ""}
                      onChange={(e) => setSenderPwd((p) => ({ ...p, [s.email]: e.target.value }))}
                      data-testid={`sender-pwd-${s.email}`}
                    />
                  </div>
                  <Button size="sm" disabled={senderBusy === s.email} onClick={() => connectSender(s.email)} data-testid={`sender-connect-${s.email}`} className="bg-emerald-600 hover:bg-emerald-700">
                    {senderBusy === s.email ? "Connecting…" : "Connect"}
                  </Button>
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-slate-400">
            Get an App Password: Google Account → Security → 2-Step Verification → App passwords (IMAP must be enabled). If a sender isn't connected, its sent copy falls back to the main connected inbox so nothing is lost.
          </p>
        </div>
      </div>

      {/* Website */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center"><Globe className="h-5 w-5 text-sky-600" /></div>
          <div>
            <h3 className="font-display text-lg font-medium">Website Form</h3>
            <p className="text-xs text-slate-500">{status?.website_leads ?? 0} leads captured so far</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Hosted enquiry form (share this link or add a button on aaywon.com)</Label>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={enquiryUrl} data-testid="enquiry-url" className="bg-slate-50 font-mono text-xs" />
              <Button variant="outline" onClick={() => copy(enquiryUrl, "Form link")} data-testid="copy-enquiry-url"><Copy className="h-4 w-4" /></Button>
              <Button variant="outline" onClick={() => window.open(enquiryUrl, "_blank")} data-testid="open-enquiry"><ExternalLink className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Tip: link your website's "Get a Quote" button to this URL — no coding needed on your site.</p>
          </div>

          <div>
            <Label className="text-xs">Or embed this form directly into your website</Label>
            <Textarea readOnly value={embedSnippet} rows={6} className="bg-slate-50 font-mono text-[11px] mt-1" data-testid="embed-snippet" />
            <Button variant="outline" size="sm" className="mt-2" onClick={() => copy(embedSnippet, "Embed code")} data-testid="copy-embed">
              <Copy className="h-4 w-4 mr-1.5" /> Copy embed code
            </Button>
          </div>
        </div>
      </div>

      {/* IndiaMART via Gmail auto-import */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center"><Store className="h-5 w-5 text-orange-600" /></div>
            <div>
              <h3 className="font-display text-lg font-medium">IndiaMART + Website (auto-import via email)</h3>
              <p className="text-xs text-slate-500">Reads IndiaMART & website quote-request emails</p>
            </div>
          </div>
          {status?.gmail_connected ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full" data-testid="gmail-connected">
              <CheckCircle2 className="h-3.5 w-3.5" /> Connected
            </span>
          ) : (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">Not connected</span>
          )}
        </div>

        {status?.gmail_connected ? (
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-700 mb-4">
              <Mail className="h-4 w-4 text-slate-500" />
              Reading IndiaMART & website lead emails from <b>{status.gmail_email}</b> — new enquiries import automatically every ~2 minutes.
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={syncNow} disabled={syncing} className="bg-blue-600 hover:bg-blue-700" data-testid="gmail-sync-now">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync Now
              </Button>
              <Button variant="outline" onClick={disconnectGmail} data-testid="gmail-disconnect" className="text-red-600">Disconnect</Button>
              {status?.gmail_last_sync && (
                <span className="text-xs text-slate-500">
                  Last sync: {new Date(status.gmail_last_sync).toLocaleString()} · {status.gmail_last_result}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 mb-4">
              <p className="font-medium text-slate-700 mb-1">Connect the Gmail inbox that receives your IndiaMART enquiry emails:</p>
              <p>1. Enable 2-Step Verification on the Google account &nbsp;2. Create an <b>App Password</b> at
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline"> myaccount.google.com/apppasswords</a>
                &nbsp;3. Paste it below. We only <b>read</b> IndiaMART emails.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Gmail address</Label>
                <Input value={gmailEmail} onChange={(e) => setGmailEmail(e.target.value)} placeholder="pranav@aaywon.com" data-testid="gmail-email-input" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3 w-3" /> App Password</Label>
                <Input value={gmailPwd} onChange={(e) => setGmailPwd(e.target.value)} placeholder="16-character app password" data-testid="gmail-pwd-input" className="font-mono text-xs" />
              </div>
            </div>
            <Button onClick={connectGmail} disabled={saving} className="bg-slate-800 hover:bg-slate-900" data-testid="gmail-connect-btn">
              {saving ? "Connecting…" : "Connect Gmail"}
            </Button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">Tip: works without the paid IndiaMART API — it reads the enquiry emails IndiaMART already sends you.</p>
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={cleanupJunk} disabled={cleaning} data-testid="cleanup-junk-btn" className="text-slate-700">
            <Trash2 className={`h-4 w-4 mr-1.5 ${cleaning ? "animate-pulse" : ""}`} /> {cleaning ? "Cleaning…" : "Clean up junk leads"}
          </Button>
          <span className="text-xs text-slate-400">Removes IndiaMART promo/system entries (e.g. "InterMESH Ltd. … Assotech", "help.indiamart.com").</span>
        </div>
      </div>


      {/* One-time historical import from any inbox */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center"><History className="h-5 w-5 text-violet-600" /></div>
          <div>
            <h3 className="font-display text-lg font-medium">One-time import from another inbox</h3>
            <p className="text-xs text-slate-500">Pull historical IndiaMART/website leads from any Gmail inbox (e.g. aaywon@gmail.com)</p>
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 mb-4">
          Generate an <b>App Password</b> for that Google account (
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">myaccount.google.com/apppasswords</a>)
          and paste it below. This runs once and imports leads from the last N days — duplicates are skipped automatically.
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="sm:col-span-1">
            <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Inbox email</Label>
            <Input value={impEmail} onChange={(e) => setImpEmail(e.target.value)} placeholder="aaywon@gmail.com" data-testid="import-email-input" />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3 w-3" /> App Password</Label>
            <Input value={impPwd} onChange={(e) => setImpPwd(e.target.value)} placeholder="16-character app password" data-testid="import-pwd-input" className="font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Last N days</Label>
            <Input type="number" value={impDays} onChange={(e) => setImpDays(e.target.value)} data-testid="import-days-input" />
          </div>
        </div>
        <Button onClick={importOnce} disabled={importing} className="bg-violet-600 hover:bg-violet-700" data-testid="import-once-btn">
          <History className={`h-4 w-4 mr-1.5 ${importing ? "animate-spin" : ""}`} /> {importing ? "Importing…" : "Import Now"}
        </Button>
        <p className="text-xs text-slate-400 mt-3">Run this once per inbox. For continuous auto-import, use "Connect Gmail" above on your main inbox.</p>
      </div>
    </div>
  );
}
